import { type ReactNode, useMemo } from "react";

import {
  Row,
  Heading,
  Column,
  Box,
  BidirectionalArrowIcon,
  IconButton,
  Tooltip,
} from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";
import { useAuditLogsEnabled } from "src/hooks/use-audit-logs-enabled";
import { MAX_WIDTH } from "src/pages/home/constants";
import { ActivityFeed } from "./activity-feed";
import { Drafts } from "./drafts";
import { useEntitlements } from "src/hooks/use-entitlement";

export const Header = ({
  children,
  view,
  onToggleView,
}: {
  children?: ReactNode;
  view: "marketing" | "activation";
  onToggleView: () => void;
}) => {
  const { user, workspace } = useUser();
  const { data: entitlementsData } = useEntitlements(true);

  const title = useMemo(() => {
    if (user?.name) {
      const names = user?.name.split(" ");
      return `Welcome back, ${names[0]}`;
    }
    return "Welcome back";
  }, [user]);

  const hasAuditLogs = useAuditLogsEnabled();

  return (
    <Column bg="white" align="center" width="100%" borderTopRadius="2xl">
      <Column maxW={MAX_WIDTH} w="100%">
        <Row align="center" justify="space-between" gap={4} mb={6}>
          <Box as={Heading} size="2xl" display={["block", "block", "none"]}>
            Welcome
          </Box>
          <Box as={Heading} size="2xl" display={["none", "none", "block"]}>
            {title}
          </Box>
          <Row gap={3}>
            {workspace?.approvals_required && <Drafts />}
            {hasAuditLogs && <ActivityFeed />}
            {entitlementsData.entitlements.audiences && (
              <Tooltip
                message={`Switch to ${
                  view === "activation" ? "Customer Studio" : "Activation"
                } view`}
                placement="top-start"
              >
                <IconButton
                  onClick={onToggleView}
                  variant="secondary"
                  aria-label="Change view."
                  icon={BidirectionalArrowIcon}
                />
              </Tooltip>
            )}
          </Row>
        </Row>
        {children}
      </Column>
    </Column>
  );
};
